import React from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { FormGroupControl, FormGroupSelect, FormGroupTypeahead } from '@components/Base';
import { FormValues } from './schema';
import { InsurancePolicyUser } from '@models/InsurancePolicyUser';
import { BusinessPartner } from '@models/BusinessPartner';
import { selectCurrentUser, selectIsHospital } from '@components/Auth';

const GROUP_PROPS = { className: 'mb-3' };

const ClaimCardBody = ({ insurancePolicyUser }: { insurancePolicyUser: InsurancePolicyUser }) => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const isHospital = useSelector(selectIsHospital);
  const {
    values: { coveredByOtherInsurance, roomStay },
    setFieldValue,
    errors,
  } = useFormikContext<FormValues>();
  const isCoveredByOtherInsurance = (coveredByOtherInsurance as unknown as string) === 'true';
  const isRoomStay = (roomStay as unknown as string) === 'true';

  return (
    <Card.Body>
      <Card.Title>{t('healthInsuranceClaimForm.injuryOrIllnessSection.title')}</Card.Title>
      <Row>
        <Col>
          <FormGroupControl
            type="datetime-local"
            name="accidentHappenedAt"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.accidentHappenedAt.label')}
            required
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroupControl
            as="textarea"
            name="symptomsDescription"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.symptomsDescription.label')}
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroupControl
            as="textarea"
            name="diagnosisDescription"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.diagnosisDescription.label')}
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroupSelect
            name="coveredByOtherInsurance"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.coveredByOtherInsurance.label')}
            groupProps={GROUP_PROPS}
            required
          >
            <option></option>
            <option value="true">{t('common:yes')}</option>
            <option value="false">{t('common:no')}</option>
          </FormGroupSelect>
        </Col>
      </Row>
      {isCoveredByOtherInsurance && (
        <Row>
          <Col>
            <FormGroupControl
              name="noteOnOtherInsurance"
              label={t('healthInsuranceClaimForm.injuryOrIllnessSection.noteOnOtherinsurance.label')}
              groupProps={GROUP_PROPS}
              required
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <FormGroupSelect
            name="roomStay"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.roomStay.label')}
            groupProps={GROUP_PROPS}
            required
          >
            <option></option>
            <option value="true">{t('common:yes')}</option>
            <option value="false">{t('common:no')}</option>
          </FormGroupSelect>
        </Col>
      </Row>
      {isRoomStay && (
        <Row>
          <Col>
            <FormGroupControl
              type="datetime-local"
              name="dischargedAt"
              label={t('healthInsuranceClaimForm.injuryOrIllnessSection.dischargeDate.label')}
              groupProps={GROUP_PROPS}
              required
            />
          </Col>
        </Row>
      )}
      {isHospital && (
        <Row>
          <Col>
            <FormGroupTypeahead
              name="businessPartnerId"
              label={t('healthInsuranceClaimForm.injuryOrIllnessSection.panelHospital.label')}
              placeholder="Type or select"
              labelKey={`name`}
              groupProps={GROUP_PROPS}
              disabled={isHospital}
              options={insurancePolicyUser?.benefitTemplate?.panelPartners ?? []}
              defaultSelectedOptions={[currentUser?.businessPartner?.name ?? '']}
              onChange={(selected) => {
                (async () => {
                  await setFieldValue('businessPartnerId', (selected[0] as BusinessPartner | undefined)?.id ?? '');
                })();
              }}
            />
          </Col>
        </Row>
      )}
      {!isHospital && (
        <Row>
          <Col>
            <FormGroupControl
              name="hospitalName"
              label={t('healthInsuranceClaimForm.injuryOrIllnessSection.hospitalName.label')}
              groupProps={GROUP_PROPS}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <FormGroupControl
            required
            name="totalClaimedAmount"
            label={t('claimForm.totalClaimedAmount.label')}
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroupControl
            required
            multiple
            type="file"
            name="medicalDocuments"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.medicalDocuments.label')}
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroupControl
            multiple
            type="file"
            name="identityDocuments"
            label={t('healthInsuranceClaimForm.injuryOrIllnessSection.identityDocuments.label')}
            groupProps={GROUP_PROPS}
          />
        </Col>
      </Row>
    </Card.Body>
  );
};

export { ClaimCardBody };
